// Turns on the phone's motion/orientation (and best-effort ambient-light)
// sensors and keeps their latest readings live. iOS gates DeviceMotion /
// DeviceOrientation behind a permission call that must run inside a user
// gesture, so enable() is invoked from the start interaction. Everything here
// degrades gracefully: a sensor that is missing or denied is simply skipped.

export interface SensorState {
  /** Device orientation in degrees (alpha compass, beta front-back, gamma left-right). */
  orientation: { alpha: number; beta: number; gamma: number } | null;
  /** Linear + gravity acceleration magnitude (m/s^2), a coarse "activity" signal. */
  motion: number;
  /** Ambient light in lux, if the (Chrome-Android, flagged) sensor is available. */
  lux: number | null;
  /** Which sensors actually came online. */
  active: string[];
}

const state: SensorState = { orientation: null, motion: 0, lux: null, active: [] };
let enabled = false;

function expose(): void {
  (window as unknown as { __sensors?: SensorState }).__sensors = state;
}

async function requestiOS(ctor: unknown): Promise<boolean> {
  // iOS 13+ exposes a static requestPermission() on the event constructor.
  const req = (ctor as { requestPermission?: () => Promise<"granted" | "denied"> })?.requestPermission;
  if (typeof req !== "function") return true; // no gate (Android/desktop)
  try {
    return (await req()) === "granted";
  } catch {
    return false;
  }
}

/** Enable all reachable sensors. Call inside a user gesture (the start tap). */
export async function enableSensors(): Promise<SensorState> {
  if (enabled) return state;
  enabled = true;
  expose();

  // Orientation.
  if (typeof DeviceOrientationEvent !== "undefined" && (await requestiOS(DeviceOrientationEvent))) {
    window.addEventListener(
      "deviceorientation",
      (e) => {
        state.orientation = { alpha: e.alpha ?? 0, beta: e.beta ?? 0, gamma: e.gamma ?? 0 };
      },
      { passive: true },
    );
    state.active.push("orientation");
  }

  // Motion (acceleration magnitude as an activity/walking signal).
  if (typeof DeviceMotionEvent !== "undefined" && (await requestiOS(DeviceMotionEvent))) {
    window.addEventListener(
      "devicemotion",
      (e) => {
        const a = e.accelerationIncludingGravity ?? e.acceleration;
        if (a) state.motion = Math.hypot(a.x ?? 0, a.y ?? 0, a.z ?? 0);
      },
      { passive: true },
    );
    state.active.push("motion");
  }

  // Ambient light (Generic Sensor API; Chrome-Android, often behind a flag).
  try {
    const ALS = (window as unknown as { AmbientLightSensor?: new () => EventTarget & { illuminance?: number; start(): void } })
      .AmbientLightSensor;
    if (ALS) {
      const s = new ALS();
      s.addEventListener("reading", () => {
        state.lux = s.illuminance ?? null;
      });
      s.start();
      state.active.push("light");
    }
  } catch {
    // permission denied / not allowed; skip
  }

  expose();
  return state;
}

export function sensors(): SensorState {
  return state;
}
